import * as Api from '@ViewModels';
import { EtlState, IEtlMetadata } from '@ViewModels';
import { css } from 'aphrodite';
import equal from 'fast-deep-equal';
import React, { useEffect } from 'react';
import { toast } from 'sonner';
import {
	invalidateFieldMappingsQuery,
	invalidateIntegrationQuery,
	invalidateUserMapsQuery,
	useDeleteExternalIntegrationFieldMappingMutation,
	useGetExternalIntegrationFieldMappingsQuery,
	useGetExternalIntegrationQuery,
	useGetExternalIntegrationUserMapsQuery,
	usePostExternalIntegrationConfigurationMutation,
	usePostExternalIntegrationFieldMappingsMutation,
	usePostExternalIntegrationKeyFactsSettingsMutation,
	usePostExternalIntegrationMainSettingsMutation,
	usePostExternalIntegrationResetIdleMutation,
	usePostExternalIntegrationStartStageMutation,
	usePostExternalIntegrationSuspendMutation,
	usePostExternalIntegrationTagsSettingsMutation,
	usePostExternalIntegrationUserMapsMutation,
} from '../../../../../../../queries';
import { Button } from '../../../../../../../web/components/Button';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../../../../../web/components/LoadingSpinner';
import { WarningIcon } from '../../../../../../../web/components/svgs/icons/WarningIcon';
import { FieldMappingsView } from '../FieldMappings';
import { UserMappingsView } from '../UserMappings';
import { ConfigurationSettingsGroup } from './components/ConfigurationSettingsGroup';
import { KeyFactSettingsGroup } from './components/KeyFactSettingsGroup';
import { MainSettingsGroup } from './components/MainSettingsGroup';
import { TagSettingsGroup } from './components/TagSettingsGroup';
import { EditModeProvider } from './context';
import { getProviderConfig } from './providers';
import { styleSheet } from './styles';

interface ISettingsProps {
	impersonationContext: Api.IImpersonationContext;
	integration: Api.IExternalIntegration;
	provider: Api.IntegrationProvider;
	setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
	editingSettings: boolean;
	setEditingSettings: (editingSettings: boolean) => void;
}

export const SettingsView: React.FC<ISettingsProps> = ({
	impersonationContext,
	integration,
	provider,
	setHasUnsavedChanges,
	editingSettings,
	setEditingSettings,
}) => {
	const showError = (error: Api.IOperationResultNoValue) => {
		setLoading(false);
		toast.error(error.systemMessage || 'Failed to save integration settings');
	};

	// Local state is null when not editing - we render from props/queries directly
	// Only populated when entering edit mode to track user changes
	const [hasChanges, setHasChanges] = React.useState(false);
	const [updatedIntegration, setUpdatedIntegration] = React.useState<Api.IExternalIntegration | null>(null);
	const [userMaps, setUserMaps] = React.useState<Api.IIntegrationUser[] | null>(null);
	const [fieldMappings, setFieldMappings] = React.useState<Api.IIntegrationFieldMapping[] | null>(null);
	const [deletedFieldMappings, setDeletedFieldMappings] = React.useState<Api.IIntegrationFieldMapping[]>([]);

	const integrationQuery = useGetExternalIntegrationQuery({
		impersonationContext,
		provider,
		meta: {
			toastErrorMessage: 'Failed to load integration settings',
		},
	});

	const userMapsQuery = useGetExternalIntegrationUserMapsQuery({
		impersonationContext,
		integrationProvider: provider,
	});

	// Derived values: use edited state if editing, otherwise use fresh data from query
	// Only use local state when actually in edit mode to avoid stale data after resets
	// Prefer integrationQuery.data over prop since prop can be stale after reset
	const displayIntegration =
		editingSettings && updatedIntegration ? updatedIntegration : (integrationQuery.data ?? integration);

	// Use displayIntegration so this updates when query data changes (e.g., after account reset)
	const salesforceContactRecordType = (displayIntegration.settings as Api.ISalesforceSettings)?.contactRecordType;
	// Only enable field mappings if there's a valid, non-Unknown contact record type
	// Wait for integrationQuery to finish fetching to avoid using stale cached data
	const shouldEnableFieldMappings =
		provider === Api.IntegrationProvider.Salesforce &&
		!displayIntegration.configuration.needsConfiguration &&
		Boolean(salesforceContactRecordType) &&
		salesforceContactRecordType !== 'Unknown' &&
		!integrationQuery.isFetching;
	const fieldMappingsQuery = useGetExternalIntegrationFieldMappingsQuery({
		impersonationContext,
		objectType: salesforceContactRecordType,
		integrationProvider: provider,
		enabled: shouldEnableFieldMappings,
		meta: {
			toastErrorMessage: 'Failed to load field mappings',
			errorLogging: {
				category: 'SettingsView',
				action: 'LoadFieldMappings-Error',
			},
		},
	});

	// Derived display values that depend on queries
	// Only use local state when actually in edit mode to avoid stale data after resets
	const displayUserMaps = editingSettings && userMaps ? userMaps : (userMapsQuery.data ?? []);
	const displayFieldMappings = editingSettings && fieldMappings ? fieldMappings : (fieldMappingsQuery.data ?? []);

	useEffect(() => {
		if (fieldMappingsQuery.error) {
			setLoading(false);
		}
	}, [fieldMappingsQuery.error]);

	useEffect(() => {
		if (integrationQuery.error) {
			setLoading(false);
		}
	}, [integrationQuery.error]);

	// Callback to track deleted field mappings from FieldMappingsView
	const onDeletedFieldMappingsChanged = React.useCallback((deletedMappings: Api.IIntegrationFieldMapping[]) => {
		setDeletedFieldMappings(deletedMappings);
	}, []);
	const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
	const [loading, setLoading] = React.useState(false);
	const [isProgressionButtonLoading, setIsProgressionButtonLoading] = React.useState(false);
	const [isCancelLoading, setIsCancelLoading] = React.useState(false);
	const [showEditConfirmationDialog, setShowEditConfirmationDialog] = React.useState(false);
	const [isExportingCustomFields, setIsExportingCustomFields] = React.useState(false);

	const userMapMutation = usePostExternalIntegrationUserMapsMutation({
		impersonationContext,
		onError: showError,
	});

	const fieldMappingsMutation = usePostExternalIntegrationFieldMappingsMutation({
		impersonationContext,
		onError: showError,
	});

	const deleteFieldMappingMutation = useDeleteExternalIntegrationFieldMappingMutation({
		impersonationContext,
		onError: showError,
	});

	const postExternalIntegrationConfigurationMutation = usePostExternalIntegrationConfigurationMutation({
		impersonationContext,
		onError: showError,
	});

	const postExternalIntegrationKeyFactsSettingsMutation = usePostExternalIntegrationKeyFactsSettingsMutation({
		impersonationContext,
		onError: showError,
	});

	const postExternalIntegrationMainSettingsMutation = usePostExternalIntegrationMainSettingsMutation({
		impersonationContext,
		onError: showError,
	});

	const postExternalIntegrationTagsSettingsMutation = usePostExternalIntegrationTagsSettingsMutation({
		impersonationContext,
		onError: showError,
	});

	const postExternalIntegrationSuspendMutation = usePostExternalIntegrationSuspendMutation({
		impersonationContext,
		onError: showError,
		onSuccess: () => invalidateIntegrationQuery({ impersonationContext, provider }),
	});

	const postExternalIntegrationResetIdleMutation = usePostExternalIntegrationResetIdleMutation({
		impersonationContext,
		onError: showError,
		onSuccess: () => invalidateIntegrationQuery({ impersonationContext, provider }),
	});

	const allStagesInState = (etl: IEtlMetadata, etlState: EtlState) => {
		return [etl.preProcess, etl.extract, etl.transform, etl.load]
			.filter(Boolean)
			.every(stage => stage.state === etlState);
	};

	const anyStagesInState = (etl: IEtlMetadata, etlState: EtlState) => {
		return [etl.preProcess, etl.extract, etl.transform, etl.load]
			.filter(Boolean)
			.some(stage => stage.state === etlState);
	};

	const postExternalIntegrationStartStageMutation = usePostExternalIntegrationStartStageMutation({
		impersonationContext,
		onSuccess: () => invalidateIntegrationQuery({ impersonationContext, provider }),
	});

	const changeFlags = React.useMemo(() => {
		// When not editing (local state is null), there are no changes
		if (!updatedIntegration) {
			return {
				configuration: false,
				settings: false,
				keyFacts: false,
				tags: false,
				userMaps: false,
				fieldMappings: false,
				hasAnyChanges: false,
			};
		}

		const hasChanged = <T,>(a: T, b: T) => !equal(a, b);
		const hasDeletedFieldMappings = deletedFieldMappings.length > 0;

		const flags = {
			configuration: hasChanged(updatedIntegration.configuration.syncUserId, integration.configuration.syncUserId),
			settings: hasChanged(updatedIntegration.settings, integration.settings),
			keyFacts: hasChanged(updatedIntegration.settings.keyFacts, integration.settings.keyFacts),
			tags: hasChanged(updatedIntegration.settings.tags, integration.settings.tags),
			userMaps: hasChanged(userMaps, userMapsQuery.data),
			fieldMappings: hasChanged(fieldMappings, fieldMappingsQuery.data) || hasDeletedFieldMappings,
		};

		const hasAnyChanges = Object.values(flags).some(Boolean);

		return {
			...flags,
			hasAnyChanges,
		};
	}, [
		integration.configuration.syncUserId,
		integration.settings,
		updatedIntegration,
		userMaps,
		userMapsQuery.data,
		fieldMappings,
		fieldMappingsQuery.data,
		deletedFieldMappings.length,
	]);

	React.useEffect(() => {
		if (editingSettings) {
			setHasChanges(changeFlags.hasAnyChanges);
			setHasUnsavedChanges(changeFlags.hasAnyChanges);
		}
	}, [editingSettings, changeFlags, setHasUnsavedChanges]);

	const refetchIntegrationData = React.useCallback(async () => {
		const result = await integrationQuery.refetch();

		// Make sure we only invalidate the query if the data has changed
		if (!equal(result.data, integration)) {
			invalidateIntegrationQuery({
				impersonationContext,
				provider,
			});
		}

		if (!result.data) {
			// Proceed with potentially stale data
			return integration;
		}

		return result.data;
	}, [impersonationContext, integration, integrationQuery, provider]);

	const clearEditState = React.useCallback(() => {
		// Clear local state - component will render from props/queries
		setUpdatedIntegration(null);
		setUserMaps(null);
		setFieldMappings(null);
		setHasChanges(false);
		setHasUnsavedChanges(false);
	}, [setHasUnsavedChanges]);

	const onEditClick = React.useCallback(async () => {
		setIsProgressionButtonLoading(true);

		// Fetch the latest data directly and use it for our check to avoid race conditions
		// This ensures we are working with the most up-to-date integration state
		const freshIntegration = await refetchIntegrationData();

		if (anyStagesInState(freshIntegration.etl, EtlState.InProgress)) {
			setShowEditConfirmationDialog(true);
		} else {
			// We do not want to blow away the suspended with error state, but still want to allow editing
			if (!anyStagesInState(freshIntegration.etl, EtlState.SuspendedWithError)) {
				await postExternalIntegrationSuspendMutation.mutateAsync({
					integrationProvider: provider,
				});
			}
			// Populate local state with current data for editing
			setUpdatedIntegration(freshIntegration);
			setUserMaps(userMapsQuery.data ?? []);
			setFieldMappings(fieldMappingsQuery.data ?? []);
			setEditingSettings(true);
		}
		setIsProgressionButtonLoading(false);
	}, [
		postExternalIntegrationSuspendMutation,
		provider,
		refetchIntegrationData,
		setEditingSettings,
		userMapsQuery.data,
		fieldMappingsQuery.data,
	]);

	const onCancelEdit = React.useCallback(async () => {
		setIsCancelLoading(true);
		// Fetch the latest data directly and use it for our check to avoid race conditions
		// This ensures we are working with the most up-to-date integration state
		const freshIntegration = await refetchIntegrationData();
		if (allStagesInState(freshIntegration.etl, EtlState.Suspended)) {
			await postExternalIntegrationResetIdleMutation.mutateAsync({
				integrationProvider: provider,
			});
		}
		setEditingSettings(false);
		// Clear local state - component will render from props/queries
		setUpdatedIntegration(null);
		setUserMaps(null);
		setFieldMappings(null);
		setDeletedFieldMappings([]);
		setHasChanges(false);
		setHasUnsavedChanges(false);
		setIsCancelLoading(false);
	}, [
		refetchIntegrationData,
		setEditingSettings,
		postExternalIntegrationResetIdleMutation,
		provider,
		setHasUnsavedChanges,
	]);

	const onSaveEdit = React.useCallback(async () => {
		setIsProgressionButtonLoading(true);
		const freshIntegration = await refetchIntegrationData();
		if (
			!allStagesInState(freshIntegration.etl, EtlState.Suspended) &&
			!anyStagesInState(freshIntegration.etl, EtlState.SuspendedWithError)
		) {
			toast.error('Integration is not suspended. Please suspend the integration before saving changes.');
			setIsProgressionButtonLoading(false);
			setLoading(false);
			return;
		}
		setIsConfirmModalOpen(true);
		setIsProgressionButtonLoading(false);
	}, [refetchIntegrationData]);

	const handleIntegrationUpdates = React.useCallback(async () => {
		let needsIntegrationInvalidation = false;
		if (changeFlags.configuration) {
			await postExternalIntegrationConfigurationMutation.mutateAsync({
				integrationProvider: provider,
				configuration: updatedIntegration.configuration,
			});

			needsIntegrationInvalidation = true;
		}

		if (changeFlags.settings) {
			await postExternalIntegrationMainSettingsMutation.mutateAsync({
				integrationProvider: provider,
				mainSettings: updatedIntegration.settings,
			});

			needsIntegrationInvalidation = true;
		}

		if (changeFlags.keyFacts) {
			await postExternalIntegrationKeyFactsSettingsMutation.mutateAsync({
				integrationProvider: provider,
				keyFactsSettings: updatedIntegration.settings.keyFacts,
			});

			needsIntegrationInvalidation = true;
		}
		if (changeFlags.tags) {
			await postExternalIntegrationTagsSettingsMutation.mutateAsync({
				integrationProvider: provider,
				tagsSettings: updatedIntegration.settings.tags,
			});
			needsIntegrationInvalidation = true;
		}

		if (needsIntegrationInvalidation) {
			invalidateIntegrationQuery({
				impersonationContext,
				provider,
			});
		}

		if (changeFlags.userMaps) {
			await userMapMutation.mutateAsync({
				integrationProvider: provider,
				userMaps,
			});

			invalidateUserMapsQuery({
				impersonationContext,
			});
		}

		if (changeFlags.fieldMappings) {
			// First, delete any field mappings marked for deletion
			for (const deletedMapping of deletedFieldMappings) {
				await deleteFieldMappingMutation.mutateAsync({
					integrationProvider: provider,
					objectType: deletedMapping.externalObjectType,
					externalFieldName: deletedMapping.externalFieldName,
				});
			}

			// Then update/create the remaining field mappings
			await fieldMappingsMutation.mutateAsync({
				integrationProvider: provider,
				fieldMappings,
			});

			invalidateFieldMappingsQuery({
				impersonationContext,
			});
		}
	}, [
		changeFlags,
		impersonationContext,
		postExternalIntegrationConfigurationMutation,
		postExternalIntegrationKeyFactsSettingsMutation,
		postExternalIntegrationMainSettingsMutation,
		postExternalIntegrationTagsSettingsMutation,
		provider,
		updatedIntegration,
		userMapMutation,
		userMaps,
		fieldMappingsMutation,
		fieldMappings,
		deleteFieldMappingMutation,
		deletedFieldMappings,
	]);

	const onSaveConfirmationRequestClose = React.useCallback(
		async (result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			setIsConfirmModalOpen(false);
			setLoading(true);

			if (result?.isDestructive && !cancel) {
				await handleIntegrationUpdates();
				const freshIntegration = await refetchIntegrationData();
				if (anyStagesInState(freshIntegration.etl, EtlState.SuspendedWithError)) {
					toast.warning(
						'Changes were saved, but no jobs were started due to the suspended with error state. Please address the errors.'
					);
					setLoading(false);
					return;
				}

				// Check if contactRecordType is Unknown and prevent pipeline start
				const salesforceSettings = freshIntegration.settings as Api.ISalesforceSettings;
				if (provider === Api.IntegrationProvider.Salesforce && salesforceSettings?.contactRecordType === 'Unknown') {
					toast.warning(
						'Settings saved successfully, but sync cannot start until a valid record type is selected. Please configure the record type in the settings.'
					);
					setLoading(false);
					setEditingSettings(false);
					setDeletedFieldMappings([]);
					clearEditState();
					return;
				}

				await postExternalIntegrationResetIdleMutation.mutateAsync({
					integrationProvider: provider,
				});

				await postExternalIntegrationStartStageMutation.mutateAsync({
					etlStage: Api.EtlStage.Extract,
					integrationProvider: provider,
					forceAll: true,
				});

				toast.success('Integration settings saved successfully');

				setEditingSettings(false);
				setDeletedFieldMappings([]);
				clearEditState();
			}
			setLoading(false);
		},
		[
			handleIntegrationUpdates,
			refetchIntegrationData,
			postExternalIntegrationResetIdleMutation,
			provider,
			postExternalIntegrationStartStageMutation,
			setEditingSettings,
			clearEditState,
		]
	);

	const onConfirmationEditClose = React.useCallback(
		async (result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			setShowEditConfirmationDialog(false);
			if (result?.isDestructive && !cancel) {
				await postExternalIntegrationSuspendMutation.mutateAsync({
					integrationProvider: provider,
				});
				// Populate local state with current data for editing
				setUpdatedIntegration(integration);
				setUserMaps(userMapsQuery.data ?? []);
				setFieldMappings(fieldMappingsQuery.data ?? []);
				setEditingSettings(true);
			}
		},
		[
			postExternalIntegrationSuspendMutation,
			provider,
			setEditingSettings,
			integration,
			userMapsQuery.data,
			fieldMappingsQuery.data,
		]
	);

	const isSalesforce = provider === Api.IntegrationProvider.Salesforce;
	const salesforceConfiguration = isSalesforce
		? (displayIntegration.configuration as Api.ISalesforceIntegrationConfiguration)
		: null;

	// Check if both Interest Tags and Topics are enabled (Salesforce-specific)
	const salesforceTagSettings = isSalesforce
		? (displayIntegration.settings.tags as Api.ISalesforceTagSettings | undefined)
		: undefined;
	const hasDualTagWarning =
		Boolean(salesforceTagSettings?.interestTagSettings?.enabled) &&
		Boolean(salesforceTagSettings?.topicSettings?.enabled);

	const hasCustomFields = (values: Record<string, string>) => {
		return values && Object.entries(values)?.length > 0;
	};

	const configWithDateFields = displayIntegration.configuration as
		| Api.IMicrosoftDynamics365SalesIntegrationConfiguration
		| Api.ISalesforceIntegrationConfiguration
		| Api.IWealthboxIntegrationConfiguration
		| Api.IClioIntegrationConfiguration;

	const hasSalesforceFields =
		Boolean(salesforceConfiguration?.availableFields) && hasCustomFields(salesforceConfiguration.availableFields);

	const hasSalesforceDateFields =
		Boolean(salesforceConfiguration?.availableDateFields) &&
		hasCustomFields(salesforceConfiguration.availableDateFields);

	const getCommaSeparatedCombinedValues = (values: Record<string, string>) => {
		return values
			? Object.entries(values)
					.map(([_, text]) => text)
					.join(',')
			: '';
	};

	const exportCustomFieldsToCsv = () => {
		setIsExportingCustomFields(true);

		const fieldsToExport: string[] = [];
		fieldsToExport.push(getCommaSeparatedCombinedValues(displayIntegration.configuration.availableCustomFields));

		if (hasCustomFields(configWithDateFields?.availableCustomDateFields)) {
			fieldsToExport.push(getCommaSeparatedCombinedValues(configWithDateFields.availableCustomDateFields));
		}

		// Salesforce has unique additional fields (availableFields, availableDateFields)
		if (isSalesforce && salesforceConfiguration) {
			if (hasSalesforceFields) {
				fieldsToExport.push(getCommaSeparatedCombinedValues(salesforceConfiguration.availableFields));
			}

			if (hasSalesforceDateFields) {
				fieldsToExport.push(getCommaSeparatedCombinedValues(salesforceConfiguration.availableDateFields));
			}
		}

		const filename = isSalesforce ? 'available_remote_fields.csv' : 'available_custom_fields.csv';
		const encodedCsv = encodeURIComponent(fieldsToExport.join(','));
		const dataUri = 'data:text/csv;charset=utf-8,' + encodedCsv;

		const link = document.createElement('a');
		link.href = dataUri;
		link.download = filename;
		link.style.display = 'none';

		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		toast.success(`Successfully exported available ${isSalesforce ? 'remote' : 'custom'} fields to a CSV file`);
		setIsExportingCustomFields(false);
	};

	const showKeyFactSettings = React.useMemo(() => {
		return getProviderConfig(provider).showKeyFacts !== false;
	}, [provider]);

	return (
		<>
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.headerSection)}>
					<div className={css(styleSheet.titleSection)}>
						<h1 className={css(styleSheet.title)}>Settings</h1>
						<div className={css(styleSheet.subtitle)}>Edit settings related to this integration</div>
					</div>
					<div className={css(styleSheet.buttonGroup)}>
						{editingSettings ? (
							<>
								<Button
									kind='primary'
									onClick={onSaveEdit}
									isLoading={isProgressionButtonLoading}
									disabled={!hasChanges || isProgressionButtonLoading || isCancelLoading}
								>
									Save
								</Button>
								<Button
									kind='reverse'
									onClick={onCancelEdit}
									isLoading={isCancelLoading}
									disabled={isProgressionButtonLoading || isCancelLoading}
								>
									Cancel
								</Button>
							</>
						) : (
							<Button kind='reverse' onClick={onEditClick} isLoading={isProgressionButtonLoading}>
								Edit
							</Button>
						)}
						{hasCustomFields(displayIntegration.configuration.availableCustomFields) ||
						hasCustomFields(configWithDateFields?.availableCustomDateFields) ||
						hasSalesforceFields ||
						hasSalesforceDateFields ? (
							<Button
								kind='reverse'
								onClick={exportCustomFieldsToCsv}
								isLoading={isExportingCustomFields}
								className={css(styleSheet.copyToClipboardButton)}
							>
								{isSalesforce ? 'Export Available Remote Fields' : 'Export Available Custom Fields'}
							</Button>
						) : null}
					</div>
				</div>

				{loading ? (
					<LoadingSpinner type='large' />
				) : (
					<EditModeProvider isEditing={editingSettings}>
						<ConfigurationSettingsGroup
							configuration={displayIntegration.configuration}
							impersonationContext={impersonationContext}
							provider={provider}
							setConfiguration={configuration => {
								setUpdatedIntegration(prev => ({
									...prev,
									configuration,
								}));
							}}
						/>
						<MainSettingsGroup
							settings={displayIntegration.settings}
							impersonationContext={impersonationContext}
							configuration={displayIntegration.configuration}
							provider={provider}
							originalIntegration={integration}
							setSettings={settings => {
								setUpdatedIntegration(prev => ({
									...prev,
									settings,
								}));
							}}
						/>

						{showKeyFactSettings && (
							<KeyFactSettingsGroup
								keyFactSettings={displayIntegration.settings.keyFacts}
								configuration={displayIntegration.configuration}
								provider={provider}
								setKeyFactsSettings={keyFacts => {
									setUpdatedIntegration(prev => ({
										...prev,
										settings: {
											...prev.settings,
											keyFacts,
										},
									}));
								}}
							/>
						)}

						<TagSettingsGroup
							tagSettings={displayIntegration.settings.tags}
							configuration={displayIntegration.configuration}
							provider={provider}
							setTagsSettings={tags => {
								setUpdatedIntegration(prev => ({
									...prev,
									settings: {
										...prev.settings,
										tags,
									},
								}));
							}}
						/>
						{/* Salesforce is the only provider with field mapping support (at the moment) */}
						{provider === Api.IntegrationProvider.Salesforce && (
							<FieldMappingsView
								impersonationContext={impersonationContext}
								fieldMappingsQuery={fieldMappingsQuery}
								fieldMappings={displayFieldMappings}
								setFieldMappings={setFieldMappings}
								externalIntegration={integration}
								onDeletedFieldMappingsChanged={onDeletedFieldMappingsChanged}
							/>
						)}
						<UserMappingsView
							impersonationContext={impersonationContext}
							userMapsQuery={userMapsQuery}
							userMaps={displayUserMaps}
							setUserMaps={setUserMaps}
						/>
					</EditModeProvider>
				)}
			</div>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isConfirmModalOpen,
					onRequestClose: onSaveConfirmationRequestClose,
				}}
				options={[
					{
						isDestructive: true,
						representedObject: true,
						title: 'Save',
					},
					{
						isCancel: true,
						representedObject: false,
						title: 'Cancel',
					},
				]}
				title='Confirm Save'
			>
				<div className={css(styleSheet.confirmationDialogDescription)}>
					Saving these changes will start a full sync. Do you want to continue with saving?
				</div>
				{hasDualTagWarning ? (
					<div className={css(styleSheet.confirmationDialogDescription, styleSheet.dualTagWarning)}>
						<strong>Note:</strong> You have both Interest Tags and Topics enabled. Salesforce recommends using one or
						the other to avoid confusion when assigning tags to records.
					</div>
				) : null}
			</ConfirmationDialog>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: showEditConfirmationDialog,
					onRequestClose: onConfirmationEditClose,
				}}
				options={[
					{
						isDestructive: true,
						representedObject: true,
						title: 'Edit',
					},
					{
						isCancel: true,
						representedObject: false,
						title: 'Cancel',
					},
				]}
				title='Confirm Edit'
			>
				<div className={css(styleSheet.confirmationDialogDescription)}>
					To safely edit the integration settings, all in progress jobs will be cancelled. Do you want to continue with
					editing?
				</div>
			</ConfirmationDialog>
		</>
	);
};
